// src/components/ModuleSelectionModal.tsx
import React, { useMemo, useState } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Title,
  SimpleGrid,
  Card,
  Button,
  Divider,
  Box,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { DisciplineBadge } from "../../components/DisciplinBadge";
import { RoleChip } from "../../components/RoleChip";
import VSpace from "../../VSpace";
import { IActiveModuleKey, MULTIPLACER_MODULES } from "./designModules";
import { changeActiveModuleThunk } from "../designThunk";

type ModuleSelectionModalProps = {
  onClose?: () => void;
};

const ROLE_BLOCK_MIN_HEIGHT = 80; // justeres ved behov for linjering

export const ModuleSelectionModal: React.FC<ModuleSelectionModalProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const activeModule = useAppSelector((s) => s.design.activeModule);

  const initialSelection: IActiveModuleKey | null = useMemo(() => {
    if (activeModule) return activeModule;
    return MULTIPLACER_MODULES[0]?.key ?? null;
  }, [activeModule]);

  const [selectedKey, setSelectedKey] = useState<IActiveModuleKey | null>(initialSelection);

  const handleSelect = (key: IActiveModuleKey) => {
    setSelectedKey(key);
  };

  const handleConfirm = () => {
    if (!selectedKey) return;
    dispatch(changeActiveModuleThunk(selectedKey));
    onClose?.();
  };

  return (
    <Modal
      opened
      onClose={() => onClose?.()}
      radius="md"
      size="calc(1100px)" // ✅ eller "calc(1200px)"
      centered
      overlayProps={{ opacity: 0.2, blur: 6 }}
      title={
        <div>
          <Title order={3}>Velg modul</Title>
          <Text size="sm" c="dimmed">
            Multiplacer er delt inn i moduler. Velg hvilken modul du vil jobbe i nå.
          </Text>
        </div>
      }
    >
      {/* Layout: scrollbart innhold + sticky footer-knapp */}
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          height: "min(78vh, 900px)", // juster ved behov
        }}
      >
        {/* Scroll-område */}
        <Box style={{ flex: 1, overflowY: "auto" }}>
          <Stack gap="lg">
            <VSpace />

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md" verticalSpacing="md">
              {MULTIPLACER_MODULES.map((module) => {
                const Icon = module.icon;
                const isSelected = module.key === selectedKey;

                return (
                  <Card
                    key={module.key}
                    withBorder
                    shadow={isSelected ? "md" : "xs"}
                    radius="md"
                    p="md"
                    onClick={() => handleSelect(module.key)}
                    style={{
                      cursor: "pointer",
                      borderWidth: isSelected ? 2 : 1,
                      borderColor: isSelected ? "var(--mantine-color-blue-6)" : "",
                      transform: isSelected ? "translateY(-2px)" : "none",
                      transition:
                        "border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
                      height: "100%", // gjør at kortene strekkes likt i grid
                    }}
                  >
                    <Stack gap="xs" h="100%">
                      {/* Topp: ikon + tittel + beskrivelse */}
                      <Stack gap="xs">
                        <Group gap="xs" align="center">
                          <Icon size={22} />
                          <Text fw={600}>{module.title}</Text>
                        </Group>

                        <Text size="sm" c="dimmed">
                          {module.description}
                        </Text>

                        <Divider my="xs" />
                      </Stack>

                      {/* Nederste del: metadata-blokk som skal linjeres likt */}
                      <Stack gap={10} style={{ marginTop: 4 }} justify="flex-start">
                        {/* Disipliner */}
                        <Stack gap={4}>
                          <Text size="xs" fw={500} c="dimmed">
                            Disipliner
                          </Text>
                          <Group gap={6}>
                            {module.disciplines.map((d) => (
                              <DisciplineBadge key={d} discipline={d} />
                            ))}
                          </Group>
                        </Stack>

                        {/* Roller – fast min-høyde slik at "Faser" starter likt i alle kort */}
                        <Stack
                          gap={4}
                          style={{
                            minHeight: ROLE_BLOCK_MIN_HEIGHT,
                          }}
                        >
                          <Text size="xs" fw={500} c="dimmed">
                            Typiske roller
                          </Text>
                          <Stack gap={4}>
                            {module.roles.map((r) => (
                              <RoleChip key={r} role={r} />
                            ))}
                          </Stack>
                        </Stack>

                        {/* Faser */}
                        <Stack gap={4}>
                          <Text size="xs" fw={500} c="dimmed">
                            Faser
                          </Text>
                          <Group gap={6}>
                            {module.phases.map((phase) => (
                              <Box
                                key={phase}
                                px={8}
                                py={4}
                                style={{
                                  fontSize: 12,
                                  borderRadius: 6,
                                  background: "var(--mantine-color-gray-1)",
                                  border: "1px solid var(--mantine-color-gray-4)",
                                }}
                              >
                                {phase}
                              </Box>
                            ))}
                          </Group>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Card>
                );
              })}
            </SimpleGrid>
          </Stack>
        </Box>

        {/* Sticky footer */}
        <Divider my="sm" />
        <Group justify="space-between" align="center">
          <Text size="sm" c="dimmed">
            Valgt modul:{" "}
            <Text span fw={600} c="dark">
              {MULTIPLACER_MODULES.find((m) => m.key === selectedKey)?.title ?? "—"}
            </Text>
          </Text>

          <Button onClick={handleConfirm} disabled={!selectedKey}>
            Aktiver modul
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
